import { apiClient } from "@/lib/api/client";
import { ENDPOINTS } from "@/lib/api/config";

export type NotificationChannel = "push" | "email" | "sms" | "in_app";

export interface NotificationReminderRequest {
  trigger: {
    kind: "absolute" | "relative";
    fire_at_ms: number;
  };
  target: {
    type: string;
    id: string;
  };
  payload?: {
    title?: string;
    body?: string;
    deep_link?: string;
    data?: Record<string, unknown>;
  };
  channels?: NotificationChannel[];
}

export interface NotificationReminderResponse {
  notification_id: string;
  status: string;
}

export interface NotificationPreferencesUpdate {
  channels?: Partial<Record<NotificationChannel, boolean>>;
  geo_opt_in?: boolean;
  daily_limit?: number;
  collection_notifications_enabled?: boolean;
  stash_ready_enabled?: boolean;
}

export interface DeviceTokenRequest {
  device_id: string;
  token: string;
  platform: "ios" | "android";
  enabled?: boolean;
}

export interface NotificationInboxItem {
  user_id?: string;
  inbox_ts: number;
  notif_id?: string;
  title?: string;
  body?: string;
  deep_link?: string;
  read?: boolean;
  created_at?: string;
}

export interface NotificationInboxResponse {
  items: NotificationInboxItem[];
  next_cursor?: string | null;
}

export interface NotificationInboxUpdateRequest {
  inbox_ts?: number[];
  mark_all?: boolean;
}

export interface NotificationUpcomingItem {
  notif_id: string;
  status?: string;
  fire_at_ms?: number;
  trigger_kind?: string;
  target_type?: string;
  target_id?: string;
  payload?: {
    title?: string;
    body?: string;
    deep_link?: string;
    data?: Record<string, unknown>;
  };
  created_at?: string;
}

export interface NotificationUpcomingResponse {
  items: NotificationUpcomingItem[];
  next_cursor?: string | null;
}

export interface NotificationCancelRequest {
  notification_id: string;
}

export interface NotificationSnoozeRequest {
  notification_id: string;
  fire_at_ms: number;
}

export interface NotificationUpcomingFilters {
  target_type?: string;
  target_id?: string;
}

export interface EmailPreferences {
  global_enabled: boolean;
  categories: Record<string, boolean>;
  unsubscribed_at: string | null;
  unsubscribe_reason: string | null;
}

export interface EmailPreferencesUpdate {
  global_enabled?: boolean;
  categories?: Record<string, boolean>;
}

export interface EmailPreferencesResponse {
  email_preferences: EmailPreferences;
  collection_notifications_enabled: boolean;
  stash_ready_enabled: boolean;
}

class NotificationsService {
  async registerDeviceToken(payload: DeviceTokenRequest): Promise<{ success: boolean }> {
    return apiClient.post<{ success: boolean }>(ENDPOINTS.NOTIFICATIONS.DEVICE_TOKEN, payload);
  }

  async createReminder(payload: NotificationReminderRequest): Promise<NotificationReminderResponse> {
    return apiClient.post<NotificationReminderResponse>(ENDPOINTS.NOTIFICATIONS.REMINDERS, payload);
  }

  async updatePreferences(payload: NotificationPreferencesUpdate): Promise<{ success: boolean }> {
    return apiClient.put<{ success: boolean }>(ENDPOINTS.NOTIFICATIONS.PREFERENCES, payload);
  }

  async getInbox(limit = 50, cursor?: string | null): Promise<NotificationInboxResponse> {
    const params = new URLSearchParams();
    if (limit) params.set("limit", String(limit));
    if (cursor) params.set("cursor", cursor);
    const query = params.toString() ? `?${params.toString()}` : "";
    return apiClient.get<NotificationInboxResponse>(`${ENDPOINTS.NOTIFICATIONS.INBOX}${query}`);
  }

  async markInboxRead(payload: NotificationInboxUpdateRequest): Promise<{ success: boolean; updated: number }> {
    return apiClient.put<{ success: boolean; updated: number }>(ENDPOINTS.NOTIFICATIONS.INBOX, payload);
  }

  async getUpcoming(
    limit = 20,
    cursor?: string | null,
    filters?: NotificationUpcomingFilters,
  ): Promise<NotificationUpcomingResponse> {
    const params = new URLSearchParams();
    if (limit) params.set("limit", String(limit));
    if (cursor) params.set("cursor", cursor);
    if (filters?.target_type) params.set("target_type", filters.target_type);
    if (filters?.target_id) params.set("target_id", filters.target_id);
    const query = params.toString() ? `?${params.toString()}` : "";
    return apiClient.get<NotificationUpcomingResponse>(`${ENDPOINTS.NOTIFICATIONS.UPCOMING}${query}`);
  }

  async cancelReminder(payload: NotificationCancelRequest): Promise<{ success: boolean }> {
    return apiClient.post<{ success: boolean }>(ENDPOINTS.NOTIFICATIONS.CANCEL, payload);
  }

  async snoozeReminder(payload: NotificationSnoozeRequest): Promise<{ success: boolean }> {
    return apiClient.post<{ success: boolean }>(ENDPOINTS.NOTIFICATIONS.SNOOZE, payload);
  }

  async getEmailPreferences(): Promise<EmailPreferencesResponse> {
    return apiClient.get<EmailPreferencesResponse>(ENDPOINTS.NOTIFICATIONS.EMAIL_PREFERENCES);
  }

  async updateEmailPreferences(payload: EmailPreferencesUpdate): Promise<{ success: boolean }> {
    return apiClient.put<{ success: boolean }>(ENDPOINTS.NOTIFICATIONS.EMAIL_PREFERENCES, payload);
  }
}

export const notificationsService = new NotificationsService();
